import { createStyles, Theme } from '@material-ui/core/styles';
import get from 'lodash/get';
import omit from 'lodash/omit';
import { styleFormIoComponent } from '#web-components/styles/form';
import ErrorIcon from '#web-components/assets/icons/error.svg';
import { COMPONENT_CLASSES } from '../../constants';

const fileSelector = `.${COMPONENT_CLASSES.file}`;

const styleCloseIcon = (color: string) => ({
  '& i': {
    '&:hover': {
      cursor: 'pointer',
      color,
    },
  },
});

export const createFileStyles = (theme: Theme) => {
  const {
    colors, spacing, typography, borders, flags,
  } = theme;
  return {
    [fileSelector]: {
      ...get(styleFormIoComponent(COMPONENT_CLASSES.file, theme), fileSelector, {}),
      overflowWrap: 'break-word',

      '& label.col-form-label, & span.col-form-label': {
        ...typography.textRegular,
        marginBottom: spacing(2),
        padding: 0,
        color: colors.text.primary,
        display: 'inline-block',
      },

      '& .list-group': {
        background: 'transparent',
        color: colors.text.primary,
        border: flags.boxStyle === 'stroke' ? 'none' : `1px solid ${colors.pageElements.table.border}`,
        borderRadius: colors.pageElements.table.corner,
      },

      '& .list-group-item': {
        ...typography.accentRegular,
        border: 'none',
        borderBottom: flags.boxStyle === 'stroke' ? `2px solid ${colors.pageElements.table.headerDivider}` : 'none',
        padding: `${spacing(3)}px ${spacing(2)}px`,
        background: 'transparent',
        color: colors.text.primary,

        '& .row': {
          marginLeft: 0,
          marginRight: 0,
          '& .col-md-1': {
            paddingLeft: 0,
          },
          '& .col-md-9': {
            paddingLeft: 0,
          },
          '& .col-md-2': {
            ...typography.textBig,
            paddingRight: 0,
            paddingLeft: 30,
            '@media (max-width:768px)': {
              paddingLeft: 0,
            },
          },
        },

        '&:last-child': {
          borderRadius: colors.pageElements.table.corner,
        },

        '& strong': {
          ...typography.accentRegular,
          color: colors.pageElements.table.headerCell.enabled.name,
        },

        '& a': {
          ...typography.textBig,
          color: colors.text.primary,
          cursor: 'pointer',
          wordBreak: 'break-word',
        },
        ...styleCloseIcon(colors.text.error),
      },
      '& .list-group-header': {
        background: colors.pageElements.table.background,
        borderRadius: colors.pageElements.table.corner,
        padding: spacing(2),
      },
      '& .list-group-header .row div:last-child': {
        minWidth: `${spacing(10)}px`,
      },
      '& .fileSelector': {
        ...typography.textRegular,
        padding: spacing(4),
        border: `2px dashed ${colors.inputs.fileUpload.dropArea.enabled.border}`,
        marginBottom: spacing(2),
        marginTop: spacing(2),
        textAlign: 'center',
        color: colors.text.primary,
        borderRadius: borders.radius1,

        '&:hover': {
          borderColor: colors.inputs.fileUpload.dropArea.hovered.border,
        },

        '& a': {
          ...typography.linkRegular,
          color: colors.navigation.textLink.enabled.text,
          display: 'block',
        },

        '& i': {
          display: 'none',
        },

        '& .loader-wrapper': {
          display: 'none',
        },
      },
      '& .text-muted:not(i)': {
        ...typography.textTiny,
        margin: 0,
        color: `${colors.text.primary} !important`,
      },

      '& .alert-warning': {
        ...typography.textTiny,
        backgroundColor: colors.layout.backgroundSecondary,
      },

      '& .progress': {
        borderRadius: `${colors.inputs.fileUpload.progressIndicator.corner} !important`,
        backgroundColor: `${colors.base.white} !important`,
      },
      '& .progress-bar': {
        borderRadius: `${colors.inputs.fileUpload.progressIndicator.corner} !important`,
        backgroundColor: `${colors.inputs.fileUpload.progressIndicator.fill} !important`,
      },

      '& .bg-info': {
        ...typography.textSmallCompact,
        backgroundColor: `${colors.inputs.fileUpload.progressIndicator.fill} !important`,
        padding: `${spacing(0.5)}px ${spacing(2)}px`,
        color: colors.text.primary,
        borderRadius: colors.inputs.fileUpload.progressIndicator.corner,
        marginBottom: spacing(2),
      },

      '& .fileName': {
        ...typography.accentRegular,
        padding: 0,
        paddingLeft: spacing(2),
        color: colors.text.primary,
        overflowWrap: 'break-word',
        wordBreak: 'break-word',

        ...styleCloseIcon(colors.text.error),
      },

      '& .fileSize': {
        ...typography.accentRegular,
        color: colors.text.primary,
        paddingBottom: spacing(1),
        paddingTop: 0,
      },
      '& .form-text.text-muted': {
        ...typography.textTiny,
        display: 'flex',
        color: `${colors.inputs.footNote.text} !important`,
        backgroundColor: colors.inputs.footNote.background,
        marginTop: spacing(1),
        paddingTop: spacing(0.5),
        paddingBottom: spacing(0.5),
        paddingLeft: spacing(1),
        paddingRight: spacing(1),
      },
      '& .formio-errors.invalid-feedback': {
        display: 'block',
        '& .error': {
          ...typography.textTiny,
          color: colors.inputs.errorMessage.text,
          position: 'relative',
          padding: `${spacing(0.5)}px ${spacing(1)}px ${spacing(0.5)}px ${spacing(4) + 3}px`,
          marginTop: spacing(0.5),

          '&:before': {
            content: '""',
            '-webkit-mask-image': `url(${ErrorIcon})`,
            maskImage: `url(${ErrorIcon})`,
            '-webkit-mask-size': 'contain',
            maskSize: 'contain',
            backgroundColor: colors.inputs.errorMessage.icon,
            position: 'absolute',
            top: spacing(0.5),
            left: spacing(1),
            width: spacing(3),
            height: spacing(3),
          },
        },
      },
      '& .field-content': {
        ...styleCloseIcon(colors.text.error),
        '& img': {
          display: 'inline-block',
          overflowWrap: 'break-word',
        },
      },
    },
    ...omit(styleFormIoComponent(COMPONENT_CLASSES.file, theme), fileSelector),
  } as Record<string, unknown>;
};

export default (theme: Theme) => createStyles({
  '@global': createFileStyles(theme),
});
